import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { Button, Card, Col, Dropdown, Form, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import SelectOption from 'components/select/SelectOption';
import { getDrpAssetCategories, getDrpAssets, getDrpPriorities, getDrpContactTypes, getDrpAssignedCompanies, getDrpInformedByUsers } from '../../../../functions/dropdowns';
import { addNewTicket } from 'functions/ticket';
import { getUsers } from '../../../../functions/user';
import TicketCreatedContent from '../TicketCreatedContent';
import { useNavigate } from 'react-router-dom';
import FileUpload from '../FileUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { setFieldValue } from 'helpers/validation';
import QuillEditor from 'components/QuilEditor';
import { toastMessageBox } from 'components/ToastifyMessage';
import { socket } from 'web_socket/socket';
import AppContext from 'context/Context';
import AddTicketCreationExtraTimeComponent from 'components/AddTicketCreationExtraTimeComponent';
import axios from 'axios';
import { date } from 'is_js';

const NewTicket = ({ }) => {
  const navigate = useNavigate();
  const { config } = useContext(AppContext);
  const { isDark } = config;
  const textColor = isDark ? '#fff' : '#212529';

  const [subject, setSubject] = useState({ value: null, options: { isRequired: true, label: 'Subject', isValidated: true, isTouched: false, type: 'string' } });
  const [description, setDescription] = useState({ value: null, options: { isRequired: true, label: 'Description', isValidated: true, isTouched: false, type: 'string' } });

  const [selectedPriority, setSelectedPriority] = useState({ value: 1, options: { isRequired: true, label: 'Priority', isValidated: true, isTouched: false, type: 'number' } });
  const [selectedCategory, setSelectedCategory] = useState({ value: null, options: { isRequired: true, label: 'Category', isValidated: true, isTouched: false, type: 'number' } });
  const [selectedContactType, setSelectedContactType] = useState({ value: 1, options: { isRequired: true, label: 'Contact Type', isValidated: true, isTouched: false, type: 'number' } });
  const [selectedAsset, setSelectedAsset] = useState({ value: null, options: { isRequired: true, label: 'Asset', isValidated: true, isTouched: false, type: 'number' } });
  const [selectedCompany, setSelectedCompany] = useState({ value: null, options: { isRequired: true, label: 'Company', isValidated: true, isTouched: false, type: 'number' } });

  const [selectedInformedByUser, setSelectedInformedByUser] = useState({ value: null, options: { isRequired: false, label: 'Informed By/Managed By', isValidated: true, isTouched: false, type: 'number' } });

  // Add email state
  const [selectedEmail, setSelectedEmail] = useState({ value: null, options: { isRequired: false, label: 'Email', isValidated: true, isTouched: false, type: 'string' } });

  const [dueDate, setDueDate] = useState({ value: null, options: { isRequired: false, label: 'Due Date', isValidated: true, isTouched: false, type: 'date' } });
  const [users, setUsers] = useState([]);

  const formFields = [
    { name: 'subject', getField: subject, setField: setSubject },
    { name: 'description', getField: description, setField: setDescription },
    { name: 'selectedPriority', getField: selectedPriority, setField: setSelectedPriority },
    { name: 'selectedCategory', getField: selectedCategory, setField: setSelectedCategory },
    { name: 'selectedContactType', getField: selectedContactType, setField: setSelectedContactType },
    { name: 'selectedAsset', getField: selectedAsset, setField: setSelectedAsset },
    { name: 'selectedCompany', getField: selectedCompany, setField: setSelectedCompany },
    { name: 'selectedInformedByUser', getField: selectedInformedByUser, setField: setSelectedInformedByUser },
    { name: 'selectedEmail', getField: selectedEmail, setField: setSelectedEmail }, // Add email to form fields
    { name: 'dueDate', getField: dueDate, setField: setDueDate },
  ];

  const [userId, setUserId] = useState(null);
  const _userData = JSON.parse(localStorage.getItem('userData'));

  useEffect(() => {
    setUserId(JSON.parse(localStorage.getItem('userId')));
  })

  // Initialize ticket creation time tracking when component mounts
  useEffect(() => {
    const initializeTicketCreationTime = async () => {
      try {
        const time = new Date().toISOString()
        setCreationStartTime(time);
      } catch (error) {
        console.error('Error initializing ticket creation time:', error);
      }
    };
    initializeTicketCreationTime();
  }, []);
  // Run once on mount

  const [showSubmitted, setShowSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTicketLoading, setIsTicketLoading] = useState(false);
  const [ticketNo, setTicketNo] = useState('');
  const [timeEntries, setTimeEntries] = useState([]); // Store time entries from the component
  const [creationStartTime, setCreationStartTime] = useState(null); // Track when user opened ticket form
  const [tempRecordId, setTempRecordId] = useState(null); // Track temp time record ID

  const { register, handleSubmit, setValue } = useForm();

  const loadDrpPriorities = async () => {
    try {
      const res = await getDrpPriorities();
      const data = res.data;
      const options = [{ value: '', text: '-Select-' }];
      {
        data &&
          data.map(d => {
            options.push({ value: d.value, text: d.text });
          });
      }
      setPrioritiesOps(options);
    } catch (err) {
      console.log('Error', err);
    }
  };

  const loadDrpAssetCategories = async () => {
    try {
      const res = await getDrpAssetCategories();
      const data = res.data;
      const options = [{ value: '', text: '-Select-' }];
      {
        data &&
          data.map(d => {
            options.push({ value: d.value, text: d.text });
          });
      }
      setCategories(options);
    } catch (err) {
      console.log('Error', err);
    }
  };

  const loadDrpAssets = async () => {
    try {
      const res = await getDrpAssets();
      const data = res.data;
      const options = [{ value: '', text: '-Select-' }];
      {
        data &&
          data.map(d => {
            options.push({ value: d.value, text: d.text });
          });
      }
      setAssets(options);
    } catch (err) {
      console.log('Error', err);
    }
  };

  const loadDrpContactType = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await getDrpContactTypes(token);
      const data = res.data;
      const options = [{ value: '', text: '-Select-' }];
      {
        data &&
          data.map(d => {
            options.push({ value: d.value, text: d.text });
          });
      }
      setContactType(options);
    } catch (err) {
      console.log('Error', err);
    }
  };

  const loadDrpAssignedCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await getDrpAssignedCompanies(token);
      const data = res.data;
      const options = [{ value: '', text: 'None' }];
      {
        data &&
          data.map(d => {
            options.push({ value: d.value, text: d.text });
          });
      }
      setCompaniessOps(options);
    } catch (err) {
      console.log('Error', err);
    }
  };

  // Updated loadUsers function
  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');

      const res = await getUsers(token);
      console.log('loadUsers recordset ', res);

      // Check if there's an error in the response
      if (res.error) {
        console.error('Error loading users:', res.error);
        return;
      }

      const usersArr = [];
      const emailOptions = [{ value: '', text: 'None' }];

      // Handle the response - it might be an array directly or wrapped in data
      const userData = Array.isArray(res) ? res : (res.data || []);

      userData.forEach(r => {
        const address = `${r.No || ''} ${r.City || ''} ${r.District || ''} ${r.PostalCode || ''}`.trim();

        usersArr.push({
          id: r.UserId,
          isActive: r.IsActive,
          userName: r.UserName,
          email: r.Email,
          displayName: r.ContactPerson,
          address: address,
          country: r.Country,
          telNo: r.TelNo,
          mobNo: r.MobNo,
          workTel: r.WorkTel,
          comment: r.Comment,
          umt: r.UMT,
          gmt: r.GMT_Offset,
          roleName: r.RoleName,
          modified: r.UpdatedDateTime,
          designation: r.Designation
        });

        // Add email to dropdown options if it exists
        if (r.Email && r.Email.trim() !== '') {
          emailOptions.push({
            value: r.Email,
            text: `${r.ContactPerson || r.UserName} - ${r.Email}`
          });
        }
      });

      setUsers(usersArr);
      setEmailOps(emailOptions);
      console.log('usersArr', usersArr);
      console.log('emailOptions', emailOptions);
    } catch (err) {
      console.log('Error in loadUsers:', err);
    }
  };

  const loadDrpInformedByUsers = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await getDrpInformedByUsers(token, companyId);
      const data = res.data;
      const options = [{ value: '', text: 'None' }];
      {
        data &&
          data.map(d => {
            options.push({ value: d.value, text: d.text });
          });
      }
      setInformedByUserOps(options);
    } catch (err) {
      console.log('Error', err);
    }
  };

  // Load all initial data including users/emails
  useEffect(() => {
    loadDrpPriorities();
    loadDrpAssetCategories();
    loadDrpAssets();
    loadDrpContactType();
    loadDrpAssignedCompanies();
    loadUsers(); // Load users and emails on mount
  }, []);

  useEffect(() => {
    if (selectedCompany.value) {
      loadDrpInformedByUsers(selectedCompany.value);
    } else {
      loadDrpInformedByUsers(0);
    }
  }, [selectedCompany.value]);

  const [prioritiesOps, setPrioritiesOps] = useState(null);
  const [categories, setCategories] = useState(null);
  const [contactType, setContactType] = useState(null);
  const [assets, setAssets] = useState(null);
  const [companiesOps, setCompaniessOps] = useState(null);
  const [InformedByUserOps, setInformedByUserOps] = useState(null);
  const [emailOps, setEmailOps] = useState([{ value: '', text: 'None' }]); // Add email options state
  const [selectedUtcTimeAndTimeZoneDetails, setSelectedUtcTimeAndTimeZoneDetails] = useState(null);

  const onGetContentHandler = (value) => {
    console.log('onGetContentHandler', value);
    setFieldValue(value, 'description', formFields);
  }

  const closeSubmittedPanel = () => {
    setShowSubmitted(false);
  }

  const onSubmitData = async event => {
    event.preventDefault();

    try {
      setIsLoading(true);
      let inValidCount = 0;

      formFields.map(f => {
        const isValidated = setFieldValue(f.getField.value, f.name, formFields);
        if (!isValidated) {
          inValidCount += 1;
        }
      });

      if (inValidCount > 0) {
        setIsLoading(false);
        return;
      }

      const fileUploadObjs = [];

      Array.from(attachments).forEach(attachment => {
        fileUploadObjs.push({ fileNo: attachment.fileNo, size: attachment.size, type: attachment.type, name: attachment.name });
      });

      console.log('fileUploadObjs-----', fileUploadObjs);

      const data = {
        contactTypeId: selectedContactType.value,
        subject: subject.value,
        priorityId: selectedPriority.value,
        ticketStatusId: 1,
        assetsMasterId: selectedAsset.value,
        assetCategoryId: selectedCategory.value,
        companyId: selectedCompany.value,
        informedByUserId: selectedInformedByUser.value,
        email: selectedEmail.value,
        dueDate: dueDate.value,
        description: description.value,
        fileUploadObjs,
      };

      const token = localStorage.getItem('token');
      console.log('data to submit', token.length);

      const res = await addNewTicket(data, token);
      console.log('ticket response', res);

      const { responseStatus, outputMessage, ticketMasterId_o, newTCKo_o } =
        res.data.ticketMasterRes.output;
      const { TicketDetailId_o } = res.data.ticketDetailRes.output;

      if (responseStatus === 'success') {
        // ✅ TICKET CREATED SUCCESSFULLY
        // Record the ticket creation completion time
        try {
          const apiPath = process.env.REACT_APP_API_PATH || 'http://localhost:8000/api';
          console.log('Recording ticket creation time for ticket:', creationStartTime);
          userId = JSON.parse(localStorage.getItem('userId'));
          console.log('Using userId:', userId);
          const recordTimeResponse = await axios.post(
            `${apiPath}/recordCreation`,
            {
              timestart: creationStartTime,
              timeend: new Date().toISOString(),
              ticketMasterId: ticketMasterId_o,
              createdByUserId: JSON.parse(localStorage.getItem('userId'))
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          if (recordTimeResponse.data.responseStatus === 'success') {
            console.log('✅ Ticket creation time recorded successfully');
          }
        } catch (timeRecordError) {
          console.error('Error recording creation time:', timeRecordError);
        }

        // Now submit time entries if any exist
        if (timeEntries && timeEntries.length > 0) {
          console.log('Submitting time entries for ticket:', ticketMasterId_o);
          const apiPath = process.env.REACT_APP_API_PATH || 'http://localhost:8000/api';

          try {
            // Submit each time entry
            const timeSubmitPromises = timeEntries.map(entry =>
              axios.post(
                `${apiPath}/time/addManual`,
                {
                  ticketMasterId: ticketMasterId_o,
                  timeMinutes: entry.timeMinutes,
                  reason: `${entry.category}${entry.description ? ' - ' + entry.description : ''}`
                },
                {
                  headers: {
                    Authorization: `Bearer ${token}`
                  }
                }
              )
            );

            await Promise.all(timeSubmitPromises);
            const totalMinutes = timeEntries.reduce((sum, e) => sum + e.timeMinutes, 0);
            console.log(`✅ Successfully added ${totalMinutes} minutes to ticket`);
          } catch (timeError) {
            console.error('Error submitting time entries:', timeError);
            // Show warning but don't fail - ticket was created
            toastMessageBox({ message: 'Ticket created but time entries could not be saved', type: 'warning' });
          }
        }

        setShowSubmitted(true);
        setTicketNo(newTCKo_o);
        toastMessageBox({ message: 'Ticket created successfully!', type: 'success' });
      }

      await socket.emit("send_action", { action: 'createTicket', room: 'notification' });

      setIsLoading(false);
      console.log('Ticket created successfully:', outputMessage);
    } catch (err) {
      setIsLoading(false);
      console.error('Error:', err);
      toastMessageBox({ message: 'Error creating ticket', type: 'error' });
    }
  };

  const onTicketCreatedPanelBackPressedHandler = () => {
    navigate('/support-desk/table-view');
    window.location.reload();
  }

  const setAttachmentHandler = (attachments) => {
    setAttachments(attachments);
  }

  const [attachments, setAttachments] = useState([]);

  const GreetingsDateInput = forwardRef(({ value, onClick }, ref) => (
    <div className="position-relative">
      <Form.Control
        size="sm"
        ref={ref}
        onClick={onClick}
        value={value}
        className="ps-4"
        onChange={e => {
          console.log({ e });
        }}
      />
      <FontAwesomeIcon
        icon="calendar-alt"
        className="text-primary position-absolute top-50 translate-middle-y ms-2"
      />
    </div>
  ));

  useEffect(() => {
    const parsedDate = moment(dueDate.value);
    const formattedDate = parsedDate.format(' [UTC]Z');
    const startMoment = moment(new Date());
    const endMoment = parsedDate;
    const duration = moment.duration(endMoment.diff(startMoment));
    const formattedDuration = `${duration.hours()} hours and ${duration.minutes()} minutes`;
    setSelectedUtcTimeAndTimeZoneDetails(`${formattedDate}`)
  }, [dueDate.value])

  const ticketForm = () => {
    return (
      <Card className="mb-3">
        <Card.Header className="bg-light" style={{ color: textColor, background: isDark ? '#23272b' : undefined }}>
          <h5 className="mb-0" style={{ color: textColor }}>New Ticket</h5>
        </Card.Header>
        <Card.Body className="bg-light" style={{ color: textColor, background: isDark ? '#23272b' : undefined }}>
          <style>{`
            .new-ticket-label { color: ${textColor} !important; }
          `}</style>
          <Row>
            <Col sm="4">
              <Form.Group
                as={Row}
                className="mb-3"
                controlId="formPlaintextPriority"
              >
                <Form.Label column sm="12" className="new-ticket-label">
                  Priority<span className="text-danger">*</span>
                </Form.Label>
                <Col sm="12">
                  <SelectOption
                    options={prioritiesOps}
                    handleChange={e => {
                      setFieldValue(
                        e.target.value,
                        'selectedPriority',
                        formFields
                      );
                    }}
                    selectedValue={selectedPriority.value}
                  />
                  {!selectedPriority.options.isValidated &&
                    selectedPriority.options.isTouched && (
                      <p
                        className="text-danger"
                        style={{ marginLeft: '10px' }}
                      >{`${selectedPriority.options.label} is required`}</p>
                    )}
                </Col>
              </Form.Group>
            </Col>
            <Col sm="8">
              <Form.Group
                as={Row}
                className="mb-3"
                controlId="formPlaintextPriority"
              >
                <Form.Label column sm="12" className="new-ticket-label">
                  Company{' '}
                  {selectedCompany.options.isRequired && (
                    <span className="text-danger">*</span>
                  )}
                </Form.Label>
                <Col sm="12">
                  <SelectOption
                    options={companiesOps}
                    handleChange={e => {
                      setFieldValue(e.target.value, 'selectedCompany', formFields);
                    }}
                    selectedValue={selectedCompany.value}
                  />
                  {!selectedCompany.options.isValidated &&
                    selectedCompany.options.isTouched && (
                      <p
                        className="text-danger"
                        style={{ marginLeft: '10px' }}
                      >{`${selectedCompany.options.label} is required`}</p>
                    )}
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col sm="4">
              <Form.Group
                as={Row}
                className="mb-3"
                controlId="formPlaintextPriority"
              >
                <Form.Label column sm="12" className="new-ticket-label">
                  Contact Type<span className="text-danger">*</span>
                </Form.Label>
                <Col sm="12">
                  <SelectOption
                    options={contactType}
                    handleChange={e => {
                      setFieldValue(
                        e.target.value,
                        'selectedContactType',
                        formFields
                      );
                    }}
                    selectedValue={selectedContactType.value}
                  />
                  {!selectedContactType.options.isValidated &&
                    selectedContactType.options.isTouched && (
                      <p
                        className="text-danger"
                        style={{ marginLeft: '10px' }}
                      >{`${selectedContactType.options.label} is required`}</p>
                    )}
                </Col>
              </Form.Group>
            </Col>

            <Col sm="4">
              <Form.Group
                as={Row}
                className="mb-3"
                controlId="formPlaintextPriority"
              >
                <Form.Label column sm="12" className="new-ticket-label">
                  Category<span className="text-danger">*</span>
                </Form.Label>
                <Col sm="12">
                  <SelectOption
                    options={categories}
                    handleChange={e => {
                      setFieldValue(
                        e.target.value,
                        'selectedCategory',
                        formFields
                      );
                    }}
                    selectedValue={selectedCategory.value}
                  />
                  {!selectedCategory.options.isValidated &&
                    selectedCategory.options.isTouched && (
                      <p
                        className="text-danger"
                        style={{ marginLeft: '10px' }}
                      >{`${selectedCategory.options.label} is required`}</p>
                    )}
                </Col>
              </Form.Group>
            </Col>
            <Col sm="4">
              <Form.Group
                as={Row}
                className="mb-3"
                controlId="formPlaintextPriority"
              >
                <Form.Label column sm="12" className="new-ticket-label">
                  Asset<span className="text-danger">*</span>
                </Form.Label>
                <Col sm="12">
                  <SelectOption
                    options={assets}
                    handleChange={e => {
                      setFieldValue(e.target.value, 'selectedAsset', formFields);
                    }}
                    selectedValue={selectedAsset.value}
                  />
                  {!selectedAsset.options.isValidated &&
                    selectedAsset.options.isTouched && (
                      <p
                        className="text-danger"
                        style={{ marginLeft: '10px' }}
                      >{`${selectedAsset.options.label} is required`}</p>
                    )}
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col sm="4">
              {_userData.roleId !== 2 && (
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId="formPlaintextSelectedInformedByUser"
                >
                  <Form.Label column sm="12" className="new-ticket-label">
                    Informed/Managed By{' '}
                    {selectedInformedByUser.options.isRequired && (
                      <span className="text-danger">*</span>
                    )}
                  </Form.Label>
                  <Col sm="12">
                    <SelectOption
                      options={InformedByUserOps}
                      handleChange={e => {
                        setFieldValue(
                          e.target.value,
                          'selectedInformedByUser',
                          formFields
                        );
                      }}
                      selectedValue={selectedInformedByUser.value}
                    />
                    {!selectedInformedByUser.options.isValidated &&
                      selectedInformedByUser.options.isTouched && (
                        <p
                          className="text-danger"
                          style={{ marginLeft: '10px' }}
                        >{`${selectedInformedByUser.options.label} is required`}</p>
                      )}
                  </Col>
                </Form.Group>
              )}
            </Col>

            {/* Add Email Dropdown */}
            <Col sm="4">
              {_userData.roleId !== 2 && (
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId="formPlaintextEmail"
                >
                  <Form.Label column sm="12" className="new-ticket-label">
                    Email{' '}
                    {selectedEmail.options.isRequired && (
                      <span className="text-danger">*</span>
                    )}
                  </Form.Label>
                  <Col sm="12">
                    <SelectOption
                      options={emailOps}
                      handleChange={e => {
                        setFieldValue(
                          e.target.value,
                          'selectedEmail',
                          formFields
                        );
                      }}
                      selectedValue={selectedEmail.value}
                    />
                    {!selectedEmail.options.isValidated &&
                      selectedEmail.options.isTouched && (
                        <p
                          className="text-danger"
                          style={{ marginLeft: '10px' }}
                        >{`${selectedEmail.options.label} is required`}</p>
                      )}
                  </Col>
                </Form.Group>
              )}
            </Col>

            <Col sm="4">
              <Form.Group
                as={Row}
                className="mb-3"
                controlId="formPlaintextPriority"
              >
                <Form.Label column sm="12" className="new-ticket-label">
                  Due Date:
                </Form.Label>
                <Col sm="12">
                  <div>
                    <div
                      style={{
                        marginRight: '10px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ width: '250px' }}>
                        <DatePicker
                          selected={dueDate.value}
                          onChange={date => {
                            setFieldValue(date, 'dueDate', formFields);
                          }}
                          showTimeSelect
                          timeFormat="h:mm aa"
                          timeIntervals={15}
                          dateFormat="MMMM d, yyyy h:mm aa"
                          timeCaption="Time"
                          customInput={<GreetingsDateInput />}
                          minDate={new Date()}
                        />
                        {!dueDate.options.isValidated &&
                          dueDate.options.isTouched && (
                            <p
                              className="text-danger"
                              style={{ marginLeft: '10px' }}
                            >{`${dueDate.options.label} is required`}</p>
                          )}
                      </div>
                      <button
                        onClick={() => {
                          setDueDate({ ...dueDate, value: null });
                        }}
                        style={{
                          position: 'relative',
                          backgroundColor: 'buttonface',
                          border: 'none',
                          color: 'red',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          marginLeft: '10px'
                        }}
                        title="Clear due date"
                      >
                        X
                      </button>
                    </div>
                  </div>
                </Col>
              </Form.Group>
            </Col>

            <Col sm="12">
              <Form.Group
                as={Row}
                className="mb-3"
                controlId="formPlaintextSubject"
              >
                <Form.Label column sm="12" className="new-ticket-label">
                  Subject:{' '}
                  {subject.options.isRequired && (
                    <span className="text-danger">*</span>
                  )}
                </Form.Label>

                <Col sm="12">
                  <Form.Control
                    type="text"
                    placeholder=""
                    onChange={e => {
                      setFieldValue(e.target.value, 'subject', formFields);
                    }}
                    value={subject.value}
                  />
                  {!subject.options.isValidated && subject.options.isTouched && (
                    <p
                      className="text-danger"
                      style={{ marginLeft: '10px' }}
                    >{`${subject.options.label} is required`}</p>
                  )}
                </Col>
              </Form.Group>
            </Col>
          </Row>

          <Row className="gx-2 gy-3">
            <Form.Group>
              <Form.Label className="new-ticket-label">
                Description<span className="text-danger">*</span>
              </Form.Label>
              <div className="create-course-description-textarea">
                <QuillEditor
                  value={description.value}
                  onQuilEditorChange={onGetContentHandler}
                />
                {!description.options.isValidated &&
                  description.options.isTouched && (
                    <p
                      className="text-danger"
                      style={{ marginLeft: '10px' }}
                    >{`${description.options.label} is required`}</p>
                  )}
              </div>
            </Form.Group>
          </Row>

          <Row className="gx-2 gy-3">
            <FileUpload setAttachmentHandler={setAttachmentHandler} />
          </Row>

          {/* Add Extra Time Tracking Component */}
          <AddTicketCreationExtraTimeComponent
            onTimeEntriesChange={(entries) => setTimeEntries(entries)}
          />

          <Row>
            <Col xs="12">
              <div className="text-center" style={{ marginTop: '40px' }}>
                <Button
                  variant="success"
                  size="lg"
                  className="me-2 mb-1"
                  onClick={onSubmitData}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      {' '}
                      <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{' '}
                      Submitting...
                    </>
                  ) : (
                    `Submit${timeEntries && timeEntries.length > 0 ? ` + Save ${timeEntries.reduce((sum, e) => sum + e.timeMinutes, 0)} min` : ''}`
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {showSubmitted ? (
        <Card className="mb-3">
          <div className="text-center">
            <TicketCreatedContent ticketNo={ticketNo} email="" onClick={onTicketCreatedPanelBackPressedHandler} />
          </div>
        </Card>
      ) : (
        ticketForm()
      )}
    </>
  );
}

export default NewTicket;